/**
 * Le calcul de la jauge du mois : axe, remplissage, seuils, couleur.
 *
 * Séparé du composant pour être exécutable hors navigateur, donc vérifiable. Ce
 * fichier ne contient QUE de l'arithmétique de positions — c'est la seule chose
 * ici qui peut rendre la jauge fausse sans que rien ne plante, et deux des
 * défauts trouvés à l'écriture (barre rouge sans charges saisies, repère
 * « objectif 0 € ») ne se voyaient dans aucun cas d'usage normal.
 *
 * Aucun import : le fichier est évalué tel quel par son script de vérification,
 * qui teste donc la source réellement livrée et non une copie de sa logique.
 */

/**
 * Un nombre de jours travaillés, à la française. L'API les pondère (un samedi
 * compte pour une fraction de journée), d'où un « 4,5 » possible.
 */
export const nbJours = (valeur) =>
  Number(valeur ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 });

/** Couleur du prévisionnel, partagée par le segment et le montant de l'en-tête. */
export const COULEUR_PREVISIONNEL = "#9ec5fe";

export const euros = (valeur) =>
  `${Math.round(valeur ?? 0).toLocaleString("fr-FR")} €`;

/**
 * L'écart entre deux montants, arrondi à partir des montants DÉJÀ arrondis.
 *
 * Arrondir chacun puis soustraire, plutôt que soustraire puis arrondir : sinon
 * « 7 442 » et « manque 2 719 » s'affichent face à des charges de « 10 160 », et
 * les trois chiffres visibles ne tombent pas juste. L'euro d'écart est
 * cosmétique, mais c'est le genre de détail qui fait douter de tout le reste.
 */
export const ecart = (a, b) => Math.round(a ?? 0) - Math.round(b ?? 0);

/**
 * Tout ce que la jauge a besoin de savoir, à partir de la réponse de l'API.
 *
 * **Un seul axe, en euros.** La première version avait deux barres — une pour
 * l'objectif, une pour les charges — ce qui obligeait à écrire le CA deux fois,
 * une par barre ; le même nombre à deux endroits se lit comme deux mesures. Ici
 * le CA est le remplissage, et l'objectif comme les charges ne sont plus que des
 * repères sur le même axe, donc directement comparables entre eux.
 */
export function construireJauge({ ca, rythme, charges }) {
  // « Renseignées » et « couvertes » sont deux questions distinctes. Les
  // confondre peignait la barre en ROUGE tant qu'aucune charge n'était saisie,
  // donc affirmait une perte qu'on ne peut pas connaître.
  const chargesConnues = charges.total_ht > 0;
  const chargesCouvertes = chargesConnues && ca.ht >= charges.total_ht;

  // Le prévisionnel : commandes pas encore livrées, livraison prévue dans le mois.
  // Il n'est qu'EMPILÉ au bout du réalisé. Le verdict, le rythme et les zones
  // restent calculés sur le réalisé seul : une commande non livrée peut encore
  // être annulée, et l'additionner annoncerait des charges couvertes qui ne le
  // sont pas. Absent de la réponse si l'API n'est pas encore à jour : 0.
  const previsionnel = Math.max(0, ca.previsionnel_ht ?? 0);

  // L'axe : le plus grand des trois montants, pour que rien ne sorte du cadre. Le
  // cas normal donne l'objectif, donc une barre qui se lit « x % de l'objectif ».
  // Un mois qui dépasse l'objectif recale l'axe sur le CA, et l'objectif devient
  // un repère franchi au lieu d'une barre pleine sans information.
  //
  // `|| 1` : sans lui, un objectif absent — configuration vidée, cache de config
  // périmé — donnerait une division par zéro.
  //
  // Réalisé + prévisionnel, et non le réalisé seul : la barre empilée ne doit
  // jamais déborder du cadre.
  const axe = Math.max(ca.objectif_ht, charges.total_ht, ca.ht + previsionnel) || 1;
  const surAxe = (montant) => (montant / axe) * 100;

  // Le rythme attendu, traduit en EUROS pour vivre sur le même axe que le reste :
  // « 89 % du mois travaillé » devient « on devrait être à 17 700 € ». C'est la
  // seule façon de le poser sur une jauge en euros sans mélanger deux grandeurs.
  const cibleRythme =
    rythme.part_attendue === null || rythme.part_attendue === undefined
      ? null
      : (ca.objectif_ht * rythme.part_attendue) / 100;

  const seuils = [];

  if (chargesConnues) {
    seuils.push({
      cle: "charges",
      part: surAxe(charges.total_ht),
      couleur: "#b02a37",
      libelle: `▲ charges ${euros(charges.total_ht)}`,
      infobulle:
        "Point d'équilibre : en dessous, le mois est en perte. Somme des charges fixes saisies dans « Charges mensuelles ».",
    });
  }

  // Le repère de rythme disparaît une fois l'objectif atteint : « où l'on devrait
  // être » n'a plus d'objet au-delà, et c'est justement là que trois étiquettes se
  // chevaucheraient, l'axe se recalant alors sur le CA.
  //
  // `> 0` en plus : un objectif absent donnerait une cible à 0 et un repère collé
  // au bord gauche, qui se lirait comme un seuil réel.
  if (cibleRythme !== null && cibleRythme > 0 && ca.ht < ca.objectif_ht) {
    seuils.push({
      cle: "rythme",
      part: surAxe(cibleRythme),
      couleur: "#6c757d",
      libelle: `▲ rythme ${euros(cibleRythme)}`,
      infobulle: `Où l'on devrait être à ce stade du mois : ${nbJours(rythme.jours_livrables_ecoules)} jours travaillés sur ${nbJours(rythme.jours_livrables_total)} (samedis non comptés).`,
    });
  }

  // L'objectif n'a besoin d'un repère que s'il n'est PAS la fin de la barre, donc
  // quand le CA ou les charges le dépassent. Sinon il est annoncé à droite du
  // cadre, où la fin de la barre le désigne déjà.
  //
  // `> 0` : sans objectif, l'axe se replie sur 1 et « 0 < 1 » ferait dessiner un
  // repère « objectif 0 € » au bord gauche.
  const objectifDansLaBarre = ca.objectif_ht > 0 && ca.objectif_ht < axe;

  if (objectifDansLaBarre) {
    seuils.push({
      cle: "objectif",
      part: surAxe(ca.objectif_ht),
      couleur: "#0d6efd",
      libelle: `▲ objectif ${euros(ca.objectif_ht)}`,
      infobulle: "Objectif de chiffre d'affaires mensuel.",
    });
  }

  // **Le remplissage est découpé en zones, pas peint d'une seule couleur.**
  //
  // Une couleur unique ne disait qu'une chose — l'état — là où le même espace peut
  // dire la composition : quelle part du chiffre d'affaires part en charges, quelle
  // part est de la marge, et ce qui dépasse l'objectif. Les trois zones portent la
  // couleur de leur repère, donc les étiquettes sous la barre servent de légende
  // sans qu'il faille en ajouter une.
  //
  // Les paliers sont TRIÉS, jamais supposés dans l'ordre : rien n'interdit des
  // charges supérieures à l'objectif — configuration bancale, mais qui doit
  // s'afficher sans se retourner.
  const paliers = [];

  if (chargesConnues) {
    paliers.push({ cle: "charges", valeur: charges.total_ht, couleur: "#b02a37" });
  }

  if (ca.objectif_ht > 0) {
    paliers.push({ cle: "objectif", valeur: ca.objectif_ht, couleur: "#0d6efd" });
  }

  paliers.sort((a, b) => a.valeur - b.valeur);

  const segments = [];
  let bas = 0;

  for (const palier of paliers) {
    const haut = Math.min(ca.ht, palier.valeur);

    if (haut > bas) {
      segments.push({
        cle: palier.cle,
        part: surAxe(haut - bas),
        couleur: palier.couleur,
      });
    }

    bas = Math.max(bas, palier.valeur);

    if (ca.ht <= palier.valeur) {
      break;
    }
  }

  // Au-delà du dernier palier : le dépassement, en vert. Sans palier du tout —
  // ni charges ni objectif — c'est la seule zone, et elle vaut tout le CA.
  if (ca.ht > bas) {
    segments.push({ cle: "au-dela", part: surAxe(ca.ht - bas), couleur: "#198754" });
  }

  // Le prévisionnel, au bout du réalisé : le bleu de l'objectif, pâle et hachuré.
  // Il se lit « pas encore acquis » sans se confondre avec les trois zones pleines.
  if (previsionnel > 0) {
    segments.push({
      cle: "previsionnel",
      part: surAxe(previsionnel),
      couleur: COULEUR_PREVISIONNEL,
      fond: `repeating-linear-gradient(135deg, ${COULEUR_PREVISIONNEL} 0 6px, #cfe2ff 6px 12px)`,
    });
  }

  // Vert dès que le rythme est tenu, et non seulement à l'objectif atteint :
  // attendre les 100 % laisserait le verdict orange 29 jours sur 30, et une
  // couleur qui ne change jamais n'informe plus.
  const dansLeRythme = cibleRythme !== null && ca.ht >= cibleRythme;

  return {
    axe,
    remplissage: surAxe(ca.ht),
    previsionnel,
    segments,
    seuils,
    objectifDansLaBarre,
    chargesConnues,
    chargesCouvertes,
    cibleRythme,
    dansLeRythme,
  };
}
